import { Fragment, ReactNode, useState } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { IconBarChartSteps, IconChartMatch as ComparisonIcon } from '../../core';
import { ReactComponent as CorrelationIcon } from '../../images/icons/charts-comparison.svg';
import { ReactComponent as DistributionIcon } from '../../images/icons/charts-distribution.svg';
import { ReactComponent as TrendIcon } from '../../images/icons/charts-trend.svg';
import { ReactComponent as ProportionIcon } from '../../images/icons/proportion.svg';

import DownloadSelect from './DownloadSelect';

export type Tag = 'proportion' | 'comparison' | 'distribution' | 'correlation' | 'trend';

interface Props {
  className?: string;
  image?: string;
  icon?: ReactNode;
  title?: string;
  url?: string;
  description?: string;
  tags?: Tag[];
}

const TREND_ICONS: Record<Tag, ReactNode> = {
  trend: (
    <div>
      <TrendIcon /> Trend
    </div>
  ),
  correlation: (
    <div>
      <CorrelationIcon /> Correlation
    </div>
  ),
  distribution: (
    <div>
      <DistributionIcon /> Distribution
    </div>
  ),
  comparison: (
    <div>
      <ComparisonIcon /> Comparison
    </div>
  ),
  proportion: (
    <div>
      <ProportionIcon /> Proportion
    </div>
  ),
};

const NO_TAGS: Tag[] = [];

// The icon every chart card shows unless the caller passes its own.
const DEFAULT_CARD_ICON = <IconBarChartSteps />;

const getIcon = (tags: Tag[]) =>
  tags.map((tag) => <Fragment key={tag}>{TREND_ICONS[tag]}</Fragment>);

const HoverableCard = ({
  className = '',
  image,
  icon = DEFAULT_CARD_ICON,
  title,
  url,
  description,
  tags = NO_TAGS,
}: Props) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const shouldShowDetails = isHovered || isSelectOpen;

  return (
    <div className={className}>
      <div
        className={`chart-block ${shouldShowDetails ? 'is-hovered' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="chart-preview">
          <div className="image-container">
            <img className="chart-img img-responsive" src={image} alt="" />
          </div>
          <div className="chart-title">
            <div className="icon-container">{icon}</div>
            {title}
          </div>
        </div>
        <div className="chart-details">
          <div className="chart-title">
            <div className="icon-container">{icon}</div>
            {title}
          </div>
          <div className="chart-description">
            <div className="top-buttons">
              <div className="type-badges">{getIcon(tags)}</div>
            </div>
            {description}
          </div>
          <div className="chart-buttons">
            <NavLink to={url ?? ''}>Details</NavLink>
            <div className="right">
              <DownloadSelect onOpenChange={setIsSelectOpen} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StyledHoverableCard = styled(HoverableCard)`
  .chart-block {
    border-radius: 8px;
    overflow: hidden;
    background: transparent;
    position: relative;
    border: 1px solid ${(props) => props.theme.neutralBorder};

    > .chart-preview {
      position: relative;
      transform: translateY(0);
      transition: transform 0.2s ease-in-out;
    }

    &:hover,
    &.is-hovered {
      .chart-preview {
        transform: translateY(-100%);
      }

      .chart-details {
        top: 0;
      }
    }

    .image-container {
      display: flex;
      align-items: center;
      height: 222px;
    }

    .type-badges {
      font-size: 0.625rem;
      line-height: 0.875rem;
      display: flex;
      align-items: center;
      gap: 8px;

      > div {
        color: ${(props) => props.theme.neutralText};
        border-radius: 4px;
        background: ${(props) => props.theme.neutralBackground};
        padding: 5px 6px;
        display: flex;
        align-items: center;

        > svg {
          padding-right: 0.375rem;
          margin-right: 0.375rem;
          border-right: 1px solid ${(props) => props.theme.neutralBorder};
          color: ${(props) => props.theme.neutralTextWeak};
          width: 1.25rem;
        }
      }
    }

    .chart-details {
      position: absolute;
      display: flex;
      flex-direction: column;
      top: 100%;
      transition: top 0.2s ease-in-out;
      height: 100%;
      width: 100%;
      z-index: 10;
      background: linear-gradient(
          to left,
          ${({ theme }) => theme.neutralBackgroundBase},
          ${({ theme }) => theme.neutralBackgroundTransparent}
        ),
        ${(props) => props.theme.neutralBackground};

      .chart-title {
        padding: 12px 12px 0;
        background: transparent;
        color: ${(props) => props.theme.neutralText};
        font-size: ${(props) => props.theme.fontSizes.base};
        line-height: ${(props) => props.theme.lineHeights.base};
        font-weight: 400;

        .icon-container {
          color: ${({ theme }) => theme.primaryTextStrong};
          background-color: ${(props) => props.theme.primaryBorder};
        }
      }

      .chart-description {
        padding: 0 12px;
        margin-bottom: 16px;
        margin-top: 8px;
        flex: 1;
        color: ${(props) => props.theme.neutralTextWeak};
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 400;
        font-size: ${(props) => props.theme.fontSizes.xs};
        line-height: ${(props) => props.theme.lineHeights.xs};

        .top-buttons {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 8px;
        }
      }

      .chart-buttons {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 12px 12px;

        .right {
          display: flex;
          gap: 0.5rem;
        }

        a {
          font-weight: 500;
          font-size: 0.875rem;
          color: ${(props) => props.theme.primaryText};
          text-decoration: none;
        }
      }
    }

    .chart-img {
      width: 100%;
      height: auto;
      display: block;
      margin: 0 auto 0.5rem;
      padding-top: 5px;
      max-width: 100%;
      object-fit: contain;
    }

    .chart-title {
      background-color: ${(props) => props.theme.neutralBackground};
      display: flex;
      align-items: center;
      color: ${(props) => props.theme.neutralText};
      font-size: ${(props) => props.theme.fontSizes.base};
      line-height: ${(props) => props.theme.lineHeights.base};
      font-weight: 400;
      padding: 8px 12px;

      .icon-container {
        margin-right: 0.5rem;
        color: ${(props) => props.theme.primaryBorder};
        padding: 0.125rem;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0rem 0.125rem 0.25rem 0.0625rem #141c2c0a; /* tokens-allow: pending design decision */
        background: ${({ theme }) => theme.neutralBackgroundBase};
        border-radius: 0.25rem;
      }
    }
  }
`;

export default StyledHoverableCard;
